import {ChangeDetectorRef, Component, ElementRef, HostBinding, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../subscribed.component";
import {activeFilterKeys, DualAxisComponent, DualAxisDelegate, DualAxisEvent, Editable} from "../dual-axis/dual-axis.component";
import {User, UserGender, UserRole, UserStatus} from "../interfaces/user";
import {Project, ProjectStatus} from "../interfaces/project";
import {Client, ClientStatus} from "../interfaces/client";
import {AuthService} from "../shared/auth.service";
import {ProjectService} from "../shared/project.service";
import {NbDialogService, NbMenuItem, NbPopoverDirective, NbTrigger} from "@nebular/theme";
import {DirectoryService} from "../shared/directory.service";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {UsersService} from "../shared/users.service";
import {ClientService} from "../shared/client.service";
import {Globals} from "../shared/Globals";
import {ColDef, ColDefDefault, ColType} from "../interfaces/colDef";
import {TreeNode} from "../interfaces/treenode";
import {
  appliedColumns,
  appliedStickyColumns,
  columns as userColumns,
  invitedColumns as userInvitedColumns,
  invitedStickyColumns,
  scheduleColumns as userScheduleColumns,
  scheduleStickyColumns as userStickyScheduleColumns,
  stickyColumns as userStickyColumns
} from "../user/coldefs-user";
import {
  columns as projectColumns,
  scheduleColumns as projectScheduleColumns,
  stickyColumns as projectStickyColumns,
  stickyScheduleColumns as projectStickyScheduleColumns,
  invitedColumns as projectInvitedColumns
} from "../project/coldefs-project";
import {
  columns as clientColumns,
  scheduleColumns as clientScheduleColumns,
  stickyColumns as clientStickyColumns,
  stickyScheduleColumns as clientStickyScheduleColumns,
  prospectColumns as clientProspectColumns
} from '../clients/coldefs-client';
import {TableOptions} from "../settings/table-options/table-options.component";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import moment from "moment";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {ProjectComponent} from "../project/project.component";
import {ClientComponent} from "../clients/client.component";
import {NewClientDialogComponent} from "../clients/new-client-dialog/new-client-dialog.component";
import {UserComponent} from "../user/user.component";
import {Paged, TableType, Type} from "../interfaces/paged";
import {Holiday} from "../controls/leave-manager/leave-manager.component";
import {FileEntry} from "../interfaces/fileEntry";
import {saveAs as importedSaveAs} from "file-saver-es";
import {NotifierService} from "../shared/notifier.service";
import {MemberAssignment} from "../interfaces/memberAssignment";
import {SearchService} from "../shared/search.service";
import {SearchDescription} from "../interfaces/searchDescription";
import {BSValidators} from "../shared/bsvalidators";
import _ from "lodash";
import {SettingsService} from "../shared/settings.service";
import {DirtyDeactivateGuard} from "../shared/dirty-deactivate-guard.service";
import {TimelineChartComponent, TimelineType} from "../charts/timeline-chart/timeline-chart.component";
import {UserProjectAssignerComponent} from "../controls/user-project-assigner/user-project-assigner.component";
import {TimeTrackingService} from "../shared/time-tracking.service";
import {TitleService} from "../shared/title.service";

type AType = User|Project|Client;

@Component({
    selector: 'bst-table-object',
    templateUrl: './table-object.component.html',
    styleUrls: ['./table-object.component.scss'],
    standalone: false
})
export class TableObjectComponent extends SubscribedComponent implements DualAxisDelegate<AType>{

  TimelineTypes = Object.values(TimelineType).filter((o) => o !== TimelineType.Year.toString());
  Globals = Globals;
  DualAxisEvent = DualAxisEvent;
  ColType = ColType;
  UserRole = UserRole;
  UserGender = UserGender;
  TableType = TableType;
  Type = Type;
  Trigger = NbTrigger;
  Math: Math = Math;

  type: TableType = null;
  setting: Type = null;
  today: Date = new Date();

  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  // Mobile (#445): stacked-card layout per row (dual-axis uses it instead of the table below 768px).
  @ViewChild('cardTemplate', {static: true, read: TemplateRef}) private _cardTemplate: TemplateRef<any>;
  // Schedule-overview rows have an inline expandable section (assign-to-project) the card mode
  // can't render, so keep that view on the table even on mobile; everything else card-ifies.
  get cardTemplateOutlet(): TemplateRef<any> {
    return this.setting === Type.ScheduleOverview ? null : this._cardTemplate;
  }

  // Card/table view toggle (per list, persisted). Cards reuse the #445 card template.
  public forceCards = true; // cards-first default, consistent with Market; overridden by saved viewmode on load
  @HostBinding('class.cards-mode') get cardsMode(): boolean { return this.forceCards && this.setting !== Type.ScheduleOverview; }

  public toggleViewMode(): void {
    this.forceCards = !this.forceCards;
    this.settings.putUserSetting('table_' + this.type + '_' + this.setting + '_viewmode', this.forceCards ? 'cards' : 'table');
    this.settings.saveUserSettings(true);
  }
  @ViewChild('openCellTemplate', {static: true, read: TemplateRef}) openCellTemplateOutlet: TemplateRef<any>;
  @ViewChild('dualAxisComponent', {static: false}) dualAxis: DualAxisComponent<AType>;
  @ViewChild('rangefieldpicker', {static: false}) rangefieldpicker: ElementRef<HTMLInputElement>;
  @ViewChild('timelineChartComponent', {static: false}) timelineChartComponent: TimelineChartComponent;
  @ViewChild('cellTemplate', {static: true}) headerCellTemplateOutlet: TemplateRef<any>;

  header?: TreeNode<any>[];
  data: TreeNode<AType>[];
  standardColumns: ColDef[] = [];
  stickyColumnsLeft: ColDef[] = [];
  stickyColumnsRight: ColDef[] = [];
  options: TableOptions = {display: [], filter: null, sorts: []};
  editable: Editable<AType> = null;
  searchform: FormGroup;
  searcher: boolean = false; // filter panel starts collapsed; open on demand (#15)
  userpending: number = 1;

  tabs: any[] = [];
  datepresents: NbMenuItem[] = [
    {
      title: 'This week',
      data: 'week'
    },
    {
      title: 'This month',
      data: 'month'
    },
    {
      title: 'This quarter',
      data: 'quarter'
    },
    {
      title: 'This year',
      data: 'year'
    },
    {
      icon: 'calendar-outline',
      title: 'Custom',
      data: 'custom'
    }
  ];

  count: number = null;
  readonly timeframe: [Date, Date] = [new Date(), new Date()];
  booted: boolean = false;
  private def: SearchDescription = null;

  public readonly dates = new FormControl({
    start: moment().startOf('week').toDate(),
    end: moment().endOf('week').toDate()
  }, []);

  public readonly timeline = new FormControl(<TimelineType>this.settings.userSetting('timeline_type', TimelineType.Day), []);

  constructor(public auth: AuthService,
              private projectService: ProjectService,
              private titleService: TitleService,
              private userService: UsersService,
              private clientService: ClientService,
              private directoryService: DirectoryService,
              private router: Router,
              private route: ActivatedRoute,
              private notifier: NotifierService,
              private searchService: SearchService,
              private dialogService: NbDialogService,
              private settings: SettingsService,
              private fb: FormBuilder,
              private dirtyDeactivate: DirtyDeactivateGuard,
              private detector: ChangeDetectorRef,
              public timeTrackingService: TimeTrackingService,
              public translate: TranslateService) {
    super();
  }

  /** Apply a schedule date-range preset from the NbPopover menu (replaces the
   * NbMenuService.onItemClick() Subject — its emission was lost to the CDK
   * overlay teardown in Chrome; a direct (click) is already in Angular's zone). */
  @ViewChild('datePop', { read: NbPopoverDirective }) datePop: NbPopoverDirective;

  public applyDatePreset(preset: string): void {
    this.datePop?.hide();
    if (preset === 'custom') {
      this.rangefieldpicker?.nativeElement?.focus();
    } else if (this.dates) {
      const start = moment().startOf(preset as any).toDate();
      const end = moment().endOf(preset as any).toDate();
      this.dates.setValue({ start, end });
    }
  }

  public get canEdit(){
    return this.auth.isAdmin || this.auth.isStaffer;
  }

  public get filterCount(): number { return activeFilterKeys(this.options?.filter).length; }

  public sortedSkills(item: any): any[] {
    const skills: any[] = item?.skills || [];
    const activeSkillIds: number[] = (this.options?.filter?.skill || []).map((s: any) => s.id);
    return [...skills].sort((a, b) => {
      const aMatched = activeSkillIds.includes(a.skill?.id) ? 0 : 1;
      const bMatched = activeSkillIds.includes(b.skill?.id) ? 0 : 1;
      if (aMatched !== bMatched) return aMatched - bMatched;
      return a.id - b.id;
    });
  }

  public get sideDefs(){
    if(this.def) {
      return this.def.colDefs.filter((o) => {
        return o.display !== 'top';
      });
    }
    return null;
  }

  selectRow($event: MouseEvent, val: TreeNode<AType>){
    if(this.setting === Type.ScheduleOverview && this.type === TableType.Users){
      $event.stopPropagation();
      this.openRow(val);
    }
  }

  openNewClientDialog(): void {
    this.dialogService.open(NewClientDialogComponent, {
      hasBackdrop: true,
      closeOnEsc: true,
      autoFocus: false,
    });
  }

  // Mobile card: tap a clamped value to expand it in place. Values that fit don't
  // toggle and don't stopPropagation, so the tap bubbles to .card-row (selectRow,
  // inert in card mode today) and link defaults instead of being hijacked.
  // stopPropagation stays inside the if on purpose; > 2 absorbs sub-pixel rounding.
  toggleCardValue($event: MouseEvent){
    const el = $event.currentTarget as HTMLElement;
    if(el.classList.contains('to-dd-open') || el.scrollHeight - el.clientHeight > 2){
      el.classList.toggle('to-dd-open');
      $event.stopPropagation();
    }
  }

  ngOnInit(): void {

    // Update values from settings
    this.searcher = this.settings.userSetting('show_table_search', false);
    const prevdates = this.settings.userSetting('overview_dates');
    if(this.dates && prevdates){
      this.dates.setValue({
        start: Globals.fromDateString(prevdates.start),
        end: Globals.fromDateString(prevdates.end)
      });
    }

    this.subs.push(this.dates.valueChanges.subscribe(async o => {
      if(this.booted && o.start && o.end && this.options?.filter && this.dualAxis) {
        this.settings.putUserSetting('overview_dates', this.range);
        await this.search();
      }
    }));

    this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
      const lasttype = this.type;
      const lastsetting = this.setting;
      this.booted = false;
      this.setting = null;
      this.type = null;
      this.header = null;

      let table = <TableType>(paramMap.get('table') || this.route.snapshot.url[0]?.path);
      let type = <Type>(paramMap.get('type'));
      if(!(Object.values(TableType).includes(table))){
        table = TableType.Users;
      }
      this.type = table;

      const path = '/' + this.type + '/view/';
      this.tabs = [
        {
          id: Type.Active,
          title: 'Active',
          route: path + Type.Active.toString(),
        }
      ];

      if(this.canEdit){
        this.tabs.push({
          id: Type.ScheduleOverview,
          title: 'Schedules',
          route: path + Type.ScheduleOverview.toString(),
        });
      }

      this.tabs.push({
        id: Type.ScheduleTimeline,
        title: 'Timeline',
        route: path + Type.ScheduleTimeline.toString(),
      });

      if(this.type === TableType.Users){
        this.tabs.splice(1, 0,
          {
            id: Type.Invited,
            title: 'Invited',
            route: path + Type.Invited.toString(),
          },
          {
            id: Type.Applied,
            title: 'Applied',
            route: path + Type.Applied.toString(),
          }
        );
      }
      else if(this.type === TableType.Projects){
        this.tabs.splice(1, 0, {
            id: Type.Pending,
            title: 'Pending',
            route: path + Type.Pending.toString(),
          },
          {
            id: Type.Invited,
            title: 'Invited',
            route: path + Type.Invited.toString(),
          }
          );
      }
      else if(this.type === TableType.Clients && this.canEdit){
        // Prospects tab: Client rows in the prospect lifecycle states (see the
        // status filter below). No icon: sibling tabs are text-only.
        this.tabs.splice(1, 0, {
          id: Type.Prospects,
          title: 'Prospects',
          route: path + Type.Prospects.toString(),
        });
      }

      if(!type){
        type = <Type>(this.settings.userSetting('table_type', Type.Active));
        return await this.router.navigateByUrl(path+type.toString());
      }
      else if(this.tabs.findIndex(o => o.id === type) === -1){
        type = this.tabs[0].id;
        return await this.router.navigateByUrl(path+type.toString());
      }

      this.setting = type;
      this.settings.putUserSetting('table_type', this.setting.toString());
      // Default to card view (consistent across all tabs); honor a saved per-table toggle when present.
      const savedViewMode = this.settings.userSetting('table_' + this.type + '_' + this.setting + '_viewmode');
      this.forceCards = savedViewMode ? savedViewMode === 'cards' : true;


      if(this.setting === Type.ScheduleOverview) {
        this.editable = null;
        switch (this.type) {
          case TableType.Clients:
            this.stickyColumnsLeft = clientStickyScheduleColumns;
            this.standardColumns = clientScheduleColumns;
            this.options.sorts = [{col: 'max_score', dir: 2}];
            break;
          case TableType.Projects:
            this.stickyColumnsLeft = projectStickyScheduleColumns;
            this.standardColumns = projectScheduleColumns;
            this.options.sorts = [{col: 'max_score', dir: 2}];
            break;
          default:
            this.stickyColumnsLeft = userStickyScheduleColumns;
            this.standardColumns = userScheduleColumns;
            this.options.sorts = [{col: 'max_score', dir: 2}];
            break;
        }
      }
      else if(this.setting === Type.Invited){
        if(this.type === TableType.Users) {
          this.stickyColumnsLeft = invitedStickyColumns;
          this.standardColumns = userInvitedColumns;
        }
        else{
          this.stickyColumnsLeft = [];
          this.standardColumns = projectInvitedColumns;
        }
        this.editable = null;
      }
      else if(this.setting === Type.Applied){
        this.stickyColumnsLeft = appliedStickyColumns;
        this.standardColumns = appliedColumns;
        this.editable = {
          view: (row: TreeNode<AType>) => {
            return this.router.navigate([this.type.toString() + '/' + row.data.id]);
          }
        };
      }
      else if(this.setting === Type.Prospects){
        // Prospect rows are now unified Client rows; clicking opens the shared
        // Client detail (which surfaces the CRM section + Promote for prospects).
        this.editable = {
          view: (row: TreeNode<AType>) => {
            return this.router.navigate(['clients', row.data.id]);
          }
        };
        this.stickyColumnsLeft = clientStickyColumns;
        this.standardColumns = clientProspectColumns;
        this.options.sorts = [];
      }
      else if(this.setting === Type.Active || this.setting === Type.Pending){
        this.editable = {
          view: (row: TreeNode<AType>) => {
            return this.router.navigate([this.type.toString() + '/' + row.data.id]);
          },
          edit: (row: TreeNode<AType>) => {
            return this.router.navigate([this.type.toString() + '/' + row.data.id], {
              queryParams: {
                edit: true
              }
            });
          },
          canArchive: (row: TreeNode<AType>) => {
            if(this.type === TableType.Clients){
              const d = <Client>(row.data);
              // Guard: prospect rows carry an OrganizationTenant id, not a
              // Client id, so they must never reach changeClientStatus.
              return d.__row_type !== 'prospect' && !d.is_tenant_root;
            }
            return true;
          },
          archive: async (row: TreeNode<AType>) => {
            let subtitle = null;
            if (this.type === TableType.Clients || this.type === TableType.Projects) {
              subtitle = row.data.name;
            } else if (this.type === TableType.Users) {
              subtitle = (<User>row.data).name_first + ' ' + (<User>row.data).name_last;
            }

            if(await this.notifier.confirm('archivecheck', subtitle)){
              if(this.type === TableType.Users) {
                const newuser = await this.userService.changeUserStatus(row.data.id, UserStatus.ARCHIVED);
                if(newuser && newuser?.user_to_client.status === UserStatus.ARCHIVED){
                  this.removerecord(row);
                }
              }
              else if(this.type === TableType.Projects){
                const newproject = await this.projectService.changeProjectStatus(row.data.id, ProjectStatus.ARCHIVED);
                if(newproject && newproject.status === ProjectStatus.ARCHIVED){
                  this.removerecord(row);
                }
              }
              else if(this.type === TableType.Clients){
                // Prospect rows can't reach here: the archive action only
                // mounts under Active/Pending (not the Prospects tab), and
                // canArchive() also rejects __row_type === 'prospect'.
                const newclient = await this.clientService.changeClientStatus(row.data.id, ClientStatus.ARCHIVED);
                if(newclient && newclient.status === ClientStatus.ARCHIVED){
                  this.removerecord(row);
                }
              }
            }
          }
        };

        if(!this.canEdit){
          delete this.editable.delete;
          delete this.editable.archive;
          delete this.editable.edit;
        }

        this.options.sorts = [];
        switch (this.type) {
          case TableType.Clients:
            this.stickyColumnsLeft = clientStickyColumns;
            // matching_description ("Matching focus") is tenant-scoped: the
            // only place it's ever written is the myclient-gated section of
            // client.component.html (the user's own tenant-root Client),
            // never a regular end-client. It stays in the shared
            // coldefs-client `columns` export (client.component.ts and
            // new-client.component.ts both need it there to register the
            // form control), but listing it here would show "Matching
            // focus" on every end-client card as if it were a per-client
            // attribute, when it can never legitimately hold a value there
            // (#57).
            this.standardColumns = clientColumns.filter(c => c.name !== 'matching_description');
            break;
          case TableType.Projects:
            this.stickyColumnsLeft = projectStickyColumns;
            this.standardColumns = projectColumns;
            break;
          default:
            this.stickyColumnsLeft = userStickyColumns;
            this.standardColumns = userColumns;
            break;
        }
      }

      this.options = {
        display: this.settings.mergeTableColumns('table_' + this.type + '_' + this.setting, this.standardColumns),
        filter: {},
        sorts: []
      };

      this.data = [];


      // bootstrap search module
      if(this.setting === Type.ScheduleOverview || this.setting === Type.ScheduleTimeline){
        if(this.type !== lasttype || (lastsetting !== Type.ScheduleTimeline && lastsetting !== Type.ScheduleOverview)){
          const options = {
            onlyfilters: true
          };

          this.searchform = null;
          this.def = await this.searchService.getDescription(this.type, options);
          await this.reset(false);
        }
      }
      else{
        this.def = null;
      }

      if(this.dualAxis && this.setting !== Type.ScheduleTimeline) {
        this.detector.detectChanges();
        this.dualAxis.mapColumns();
        await this.search();
      }

      await this.titleService.newTitle(this.type);

      this.booted = true;
    }));
  }

  private removerecord(row: any) {
    const index = this.data.indexOf(row);
    if(index !== -1){
      this.data.splice(index, 1);
      if(this.count){
        this.count -= 1;
      }
      return true;
    }
    return false;
  }

  async paged(page: number = 1): Promise<Paged<AType>> {
    if(!this.setting || !this.type){
      return null;
    }

    this.loading.next(true);

    let filters = this.options.filter || {};
    if(this.setting == Type.ScheduleOverview){
      filters.dates = this.range;
      this.timeframe[0] = this.dates.value.start;
      this.timeframe[1] = this.dates.value.end;
    }
    else {
      this.header = null;
      switch (this.type) {
        case TableType.Projects:
          filters.status = this.setting === Type.Pending ? ProjectStatus.PENDING : ProjectStatus.ACTIVE;
          break;
        case TableType.Clients:
          // Prospects = the three prospect lifecycle states (a Client row IS the
          // prospect now; the old OrganizationTenant 'tracked' arm was removed).
          // The Client queryBuilder accepts a status array (whereIn). Active = 1.
          filters.status = this.setting === Type.Prospects
            ? [ClientStatus.PROSPECT, ClientStatus.PROSPECT_ON_HOLD, ClientStatus.PROSPECT_LOST]
            : ClientStatus.ACTIVE;
          break;
        default:
          filters.status = this.setting === Type.Pending ? UserStatus.PENDING : UserStatus.ACTIVE;
          break;
      }
    }

    let directory;
    if(this.setting === Type.ScheduleOverview){
      const s = this.options.sorts && this.options.sorts.length ? this.options.sorts[0] : null;
      const params = this.params;
      if(params) {
        filters = {...filters, ...params};
      }

      if(this.type === TableType.Projects){
        directory = await this.projectService.findSchedules(page, filters, s);
      }
      else if(this.type === TableType.Clients){
        directory = await this.clientService.findSchedules(page, filters, s);
      }
      else{
        directory = await this.userService.findSchedules(page, filters, s);
      }
    }
    else if(this.setting === Type.Invited){
      if(this.type === TableType.Users) {
        directory = await this.userService.invitedUsers(page, filters, this.options.sorts);
      }
      else{
        directory = await this.projectService.invitedProjects(page, filters, this.options.sorts);
      }
    }
    else if(this.setting === Type.Applied){
      directory = await this.userService.appliedUsers(page, filters, this.options.sorts);
    }
    else{
      if(this.type === TableType.Projects){
        directory = await this.projectService.findProjects(page, filters, this.options.sorts);
      }
      else if(this.type === TableType.Clients){
        directory = await this.clientService.findClients(page, filters, this.options.sorts);
      }
      else{
        directory = await this.userService.findUsers(page, filters, await this.dualAxis?.sorts());
      }
    }

    if(page <= 1){
      this.data = [];
    }

    this.options.filter = filters;
    this.loading.next(false);
    if(directory && directory.data){
      this.count = directory.total;
      if(!_.isEmpty(directory.summary)){
        this.header = [TableObjectComponent.toNode(directory.summary)];
      }
      this.data = this.data.concat(directory.data.map((o) => this.transform(o)));
      return directory;
    }
    this.count = null;
    return null;
  }

  private static toNode(item: AType){
    return <TreeNode<AType>>{
      data: item,
      expanded: false,
      children: []
    };
  }

  private transform(o: AType): TreeNode<AType>{
    if(this.type === TableType.Users && (this.setting === Type.Active || this.setting === Type.Pending)){
      (<User>o).holidays.sort((a: Holiday, b: Holiday) => {
        return Globals.fromDateString(b.end_on).getTime() - Globals.fromDateString(a.end_on).getTime();
      });
    }
    else if(this.type === TableType.Projects && this.setting === Type.Active){
      const s = Globals.fromDateString((<Project>o).start_on);
      const e = Globals.fromDateString((<Project>o).end_on);
      this.timeframe[0] = !this.timeframe[0] || s < this.timeframe[0] ? s : this.timeframe[0];
      const endon = e || new Date();
      this.timeframe[1] = !this.timeframe[1] || endon > this.timeframe[1] ? endon : this.timeframe[1];
    }
    return TableObjectComponent.toNode(o);
  }

  // private async formattedSorts(){
  //   const _sorts:{col: string, dir: number, meta?: any}[] = [];
  //   for(let s of this.sorts){
  //     const col = this.standardColumns.concat(this.stickyColumnsLeft).find(si => si.name === s.col);
  //     if(col) {
  //
  //       let location: LatLng = null;
  //       if(col.type === ColType.LOCATION){
  //         this.loading.next(true);
  //         location = await Globals.getLocation();
  //         this.loading.next(false);
  //       }
  //
  //       if (col.sort instanceof Array) {
  //         for(let so of col.sort){
  //           _sorts.push({col: so, dir: s.dir, meta: location});
  //         }
  //       } else if (col.name) {
  //         _sorts.push({
  //           col: col.name,
  //           dir: s.dir,
  //           meta: location
  //         });
  //       }
  //     }
  //   }
  //   return _sorts;
  // }

  private get range(): {start: string, end: string}{
    return {
      start: Globals.toISODate(this.dates.value.start),
      end: Globals.toISODate(this.dates.value.end)
    };
  }

  // logo(item: User|Client) {
  //   return item.logo && item.logo.preview ? Globals.storage(item.logo.preview) : ((<User>item).provider_avatar || null);
  // }

  preview(item: AType) {
    let comp;
    switch (this.type) {
      case TableType.Projects:
        comp = ProjectComponent;
        break;
      case TableType.Clients:
        comp = ClientComponent;
        break;
      default:
        comp = UserComponent;
        break;
    }

    const open = this.dialogService.open<PopupDialogComponent<typeof comp>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        comp: comp,
        willclose: (popup) => {
          return this.dirtyDeactivate.warnIfNeeded(<SubscribedComponent>popup.instance);
        },
        componentContext: {
          height: '300px',
          couldEdit: true,
          couldArchive: false,
          couldAssign: this.range ?? null,
          id: item.id
        }
      }
    });

    const suba = open.onClose.subscribe(async (res: typeof comp) => {
      suba.unsubscribe();
      if(res.touched){
        return await this.search();
      }
    });
  }

  /**
   * Prospect lifecycle key for the Prospects-tab status pill. The row carries
   * the numeric Client `status`; map it to a stable key that drives both the
   * `rowtype.*` translate label and the colour class. Ported from the (now
   * removed) ClientsComponent.badgeKey so the lifecycle reads at a glance.
   */
  public prospectStatusKey(client: Client): string {
    switch (client.status) {
      case ClientStatus.PROSPECT:         return 'prospect';
      case ClientStatus.PROSPECT_ON_HOLD: return 'on_hold';
      case ClientStatus.PROSPECT_LOST:    return 'lost';
      case ClientStatus.ARCHIVED:         return 'archived';
      default:                            return 'client';
    }
  }

  public cv(user: User){
    return (user.file_entries ? user.file_entries.find(o => o.tag === 'cv') : null) || null;
  }

  public getcv(cv: FileEntry){
    this.userService.download(cv.id).then((item: Blob) => {
      importedSaveAs(item, cv.name || 'cv.' + cv.extension);
      return this.notifier.notify(cv.name || 'cv', 'uploaddownload', 'primary', 'file-outline');
    }).catch((error) => {
      console.error(error);
      return this.notifier.notify('error', null, 'danger');
    });
  }

  public lastHoliday(user: User){
    if(user.holidays && user.holidays.length){
      let top: Holiday = null;
      for(let holiday of user.holidays){
        if(holiday.end_on <= this.today){
          break;
        }
        top = holiday;
      }
      return top;
    }
    return null;
  }

  public memberList(items: MemberAssignment[]): string{
    return items.map(o => {
      if(o.user){
        return o.user.name_first + ' ' + o.user.name_last;
      }
      return null;
    }).filter(o => o).join(' , ');
  }

  public totalhours(members: MemberAssignment[]) {
    return members.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.dedication;
    }, 0);
  }

  public get headerTitle(){
    return null;
  }

  /** Which hero (if any) the current entity shows in card view. */
  public get cardHeroKind(): 'project' | null {
    return this.type === TableType.Projects ? 'project' : null;
  }

  /** Project timeline: elapsed start_on -> end_on, clamped 0-100. show=false when undatable. */
  public projectTimeline(row: any): { pct: number; show: boolean } {
    const s = row?.start_on ? Globals.fromDateString(row.start_on)?.getTime() : NaN;
    const e = row?.end_on   ? Globals.fromDateString(row.end_on)?.getTime()   : NaN;
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) { return { pct: 0, show: false }; }
    const pct = Math.max(0, Math.min(100, Math.round(((Date.now() - s) / (e - s)) * 100)));
    return { pct, show: true };
  }

  public projectIsActive(row: any): boolean { return row?.status === ProjectStatus.ACTIVE; }

  async reset(andsearch: boolean = true){
    if(this.def) {
      const group = {};
      if(!this.searchform){
        this.def.colDefs.forEach((col) => {
          group[col.name] = [ColDefDefault(col), (col.required ? [BSValidators.required] : [])];
        });
        this.searchform = this.fb.group(group);
      }
      else{
        this.def.colDefs.forEach((col) => {
          group[col.name] = ColDefDefault(col);
        });

        this.searchform.setValue(group);
      }
    }
    else{
      this.searchform = null;
    }

    this.detector.detectChanges();
    if(andsearch) {
      await this.search();
    }
  }

  private get params() {
    if (this.searchform) {
      const params = _.cloneDeep(this.searchform.getRawValue());
      for (let p in params) {
        if (params.hasOwnProperty(p) && params[p]) {
          if (params[p].start instanceof Date && params[p].end instanceof Date) {
            params[p].start = Globals.toISODate(params[p].start);
            params[p].end = Globals.toISODate(params[p].end);
          } else if (params[p] instanceof Date) {
            params[p] = Globals.toISODate(params[p]);
          }
        }
      }
      return params;
    }
    return null;
  }

  public async search() {
    if(this.dualAxis && this.setting !== Type.ScheduleTimeline){
      return await this.dualAxis.reset();
    }
    else if(this.timelineChartComponent && this.setting === Type.ScheduleTimeline){
      return await this.timelineChartComponent.refresh();
    }
  }

  get filters(){
    return {...this.options.filter, ...this.params};
  }

  async toggleSearcher() {
    if(this.searcher){
      this.searcher = false;
      await this.reset();
    }
    else{
      this.searcher = true;
    }
    this.settings.putUserSetting('show_table_search', this.searcher);
  }

  keyup($event: KeyboardEvent) {
    if($event.key === 'Enter' && !$event.shiftKey){
      $event.preventDefault();
      this.search().catch(console.error);
    }
  }

  async updateCenter($event: Date) {
    if(!this.isLoading && this.timelineChartComponent){
      this.timelineChartComponent.center = $event;
      await this.search();
    }
  }

  public caninvite(invited_at){
    return moment(invited_at) < moment().subtract(20, 'hour');
  }

  public async invite(item: TreeNode<any>){
    const did = await this.userService.reinviteUser(item.data.id);
    if(did){
      item.data.invited_at = Date.now();
      item.data.invites += 1;
    }
  }

  async uninvite(item: TreeNode<any>) {
    const trans = await this.translate.get('inviterevoke', {value: item.data.email}).toPromise();
    if(await this.notifier.confirm('sure?', trans, 'revokeinvite', 'danger')){
      if(await this.userService.uninviteUser(item.data.id)){
        this.removerecord(item);
      }
    }
  }

  async approve_project(item: TreeNode<User>) {
    this.loading.next(true);
    const did = await this.projectService.approveAssignment(item.data.id);
    if(did){
      this.removerecord(item);
      await this.notifier.notify('projectapproved', 'success', 'success', 'checkmark-circle-2-outline');
    }
    this.loading.next(false);
  }

  async reject_project(item: TreeNode<User>){
    this.loading.next(true);
    const did = await this.projectService.rejectAssignment(item.data.id);
    if(did){
      this.removerecord(item);
      await this.notifier.notify('projectrejected', 'success', 'success', 'close-circle-outline');
    }
    this.loading.next(false);
  }

  async approve(item: TreeNode<User>) {
    this.loading.next(true);
    const did: User = await this.userService.approveUser(item.data.id);
    if(did){
      this.removerecord(item);
      await this.notifier.notify('userapproved', 'success', 'success', 'checkmark-circle-2-outline');
    }
    this.loading.next(false);
  }

  async reject(item: TreeNode<User>){
    this.loading.next(true);
    const did = await this.userService.rejectUser(item.data.id);
    if(did){
      this.removerecord(item);
      await this.notifier.notify('userrejected', 'success', 'success', 'close-circle-outline');
    }
    this.loading.next(false);
  }

  openRow(node: TreeNode<AType>) {
    node.expanded = !node.expanded;
  }

  async openAssignmentDialog(item){
    const hours = Math.trunc(item.delta_hours || 0);
    return await UserProjectAssignerComponent.assignmentDialog(this.directoryService, this.dialogService, this.projectService,
      item.user,
      this.range, hours > 1 ? hours : null, this.loading);
  }

  formattedHighlights(item){
    if(item.highlights) {
      const max_highlights = 5;
      const highs = [].concat(item.highlights.cv || []).concat(item.highlights.career || []).concat(item.highlights.school || []);
      return highs.slice(0, max_highlights);
    }
    return null;
  }
}
